import { Box } from "tldraw";
import {
  PAGE_HEIGHT,
  PAGE_WIDTH,
} from "../../components/CanvasPaperBackground";
import { debugLog } from "@/utils/debugLog";
import { BaseAgentManager } from "./BaseAgentManager";

const DEBOUNCE_MS = 300;
const PAN_DURATION_MS = 350;
/**
 * Below this, a pan is not worth animating — the new content is effectively
 * centred already and moving would read as jitter, not as following.
 */
const MIN_PAN_TRAVEL_PX = 24;

const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

export class AgentViewportFollowManager extends BaseAgentManager {
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private accumulatedBox: Box | null = null;
  /**
   * Whether a request is in flight. Following is only live between
   * `onRequestStart` and `onRequestEnd`.
   *
   * Without this the manager would keep accepting positions after the turn
   * ended: a late-arriving action would start a fresh debounce and pan the
   * student somewhere while nothing was happening, or worse, land in the gap
   * before the next turn began. The old code got this for free from the
   * fire-once latch; now that a turn may pan several times, the window has to
   * be tracked explicitly.
   */
  private active = false;

  /**
   * Called from the action loop with the page coordinates of each
   * spatial action. Accumulates into a bounding box and triggers
   * a viewport adjustment on each debounce flush.
   */
  onActionPosition(pos: { x: number; y: number } | null): void {
    if (!this.active || !pos) return;

    if (this.accumulatedBox) {
      const minX = Math.min(this.accumulatedBox.minX, pos.x);
      const minY = Math.min(this.accumulatedBox.minY, pos.y);
      const maxX = Math.max(this.accumulatedBox.maxX, pos.x);
      const maxY = Math.max(this.accumulatedBox.maxY, pos.y);
      this.accumulatedBox.set(minX, minY, maxX - minX, maxY - minY);
    } else {
      this.accumulatedBox = new Box(pos.x, pos.y, 0, 0);
    }

    // Restart debounce timer
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.flushPan(), DEBOUNCE_MS);
  }

  /**
   * Centre the viewport on what the agent just drew.
   *
   * Runs on EVERY debounce flush, not once per request: the contract is that
   * the camera follows the agent to each thing it places, so a turn that draws
   * in three places pans three times. The trailing debounce keeps that sane —
   * a burst of ops restarts the timer and resolves to a single pan once the
   * burst settles, so this is roughly once per visual beat, not once per op.
   *
   * It also pans when the new content is ALREADY on screen. An earlier version
   * moved only when the content fell OUTSIDE the viewport, which meant the
   * common case — the agent writing the next step into the column the student
   * is already looking at — never moved the camera, and the feature looked
   * broken.
   */
  private flushPan(): void {
    this.debounceTimer = null;
    if (!this.active || !this.accumulatedBox) return;

    const { editor } = this.agent;
    const centre = this.accumulatedBox.center;
    const cam = editor.getCamera();
    const vsb = editor.getViewportScreenBounds();
    const z = cam.z;

    // The camera position that would put `centre` in the middle of the
    // viewport, CLAMPED to the page. `centerOnPoint` does not clamp, and the
    // elastic overscroll spring in TldrawCanvasAgent immediately drags the
    // camera back inside on the next tick with `force: true` — which both
    // undoes the pan and kills the in-flight animation.
    //
    // That is why auto-follow appeared dead for content near the top of the
    // sheet: centring page y=92 (rule line 1) asks for camera.y = +358, the
    // spring caps camera.y at 0, and the net movement is zero. The whole first
    // ~6 ruled lines are unreachable that way — which is exactly where a lesson
    // starts. Clamping here asks only for what the page can actually give, so
    // content above the fold lands at the TOP of the viewport rather than not
    // moving at all.
    const lowerX = Math.min(0, vsb.w / z - PAGE_WIDTH);
    const lowerY = Math.min(0, vsb.h / z - PAGE_HEIGHT);
    const targetX = clamp(vsb.w / (2 * z) - centre.x, lowerX, 0);
    const targetY = clamp(vsb.h / (2 * z) - centre.y, lowerY, 0);

    // Threshold on the movement the camera can ACTUALLY make, not on the
    // distance to the content. Those differ precisely in the clamped case, and
    // measuring the latter would keep re-animating a camera that cannot move.
    const travel = Math.hypot(targetX - cam.x, targetY - cam.y);
    const worthMoving = travel >= MIN_PAN_TRAVEL_PX;

    debugLog("[ViewportFollow] flushPan", {
      accumulatedBox: {
        x: this.accumulatedBox.x,
        y: this.accumulatedBox.y,
        w: this.accumulatedBox.w,
        h: this.accumulatedBox.h,
      },
      centre: { x: centre.x, y: centre.y },
      camera: {
        from: { x: Math.round(cam.x), y: Math.round(cam.y) },
        to: { x: Math.round(targetX), y: Math.round(targetY) },
      },
      travel: Math.round(travel),
      decision: worthMoving ? "PAN" : "ALREADY THERE",
    });

    if (worthMoving) {
      editor.setCamera(
        { x: targetX, y: targetY, z },
        { animation: { duration: PAN_DURATION_MS } },
      );
    }

    // Start the next batch empty, so the following pan centres on the NEXT
    // elements rather than on the union of everything drawn this turn — which
    // would drift toward the centroid of the whole board as a turn went on.
    this.accumulatedBox = null;
  }

  onRequestStart(): void {
    this.active = true;
    this.accumulatedBox = null;
    this.clearDebounceTimer();
  }

  /**
   * Called when a request ends (success, cancel, or error). Flushes the pan
   * the debounce is still holding.
   *
   * This flush is load-bearing: the gateway ships a turn's resolved actions as
   * a back-to-back burst of CANVAS_ACTION chunks, so successive
   * `onActionPosition` calls land far closer together than DEBOUNCE_MS and keep
   * restarting the timer. A turn therefore usually ends with a pan still
   * pending, and an earlier version CANCELLED it here instead of running it —
   * so the student was never moved to what the agent had just written.
   */
  onRequestEnd(): void {
    this.clearDebounceTimer();
    // Flush BEFORE closing the window — this is the turn's last and most
    // important pan, and `flushPan` is a no-op once `active` is false.
    this.flushPan();
    this.active = false;
  }

  private clearDebounceTimer(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  reset(): void {
    this.active = false;
    this.accumulatedBox = null;
    this.clearDebounceTimer();
  }

  dispose(): void {
    this.reset();
    super.dispose();
  }
}
